import React from 'react';

import * as Yup from 'yup';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import CrudForm from '../../../Form/CrudForm';
import TitleWithDeleteButton from '../../../TitleWithDeleteButton';

import { LOAN_FILE_DOCUMENT_TYPES } from '../../../../constants/formOptions';
import LoanFileService from '../../../../services/LoanFileService';
import { useUiStore } from '../../../../store';

export default function DocumentForm({
  file,
  setFiles,
  loanFileId,
  disabled,
  refetch,
  loanFile,
}) {
  const { documentId } = useParams();

  const notify = useUiStore((state) => state.notify);

  const { data: document, refetch: refetchDocument } = useQuery({
    queryKey: ['loanFileDocument', documentId],
    queryFn: () =>
      LoanFileService.getLoanFileDocument({
        id: loanFileId,
        documentId,
      }),
    enabled: !!documentId,
  });

  const borrowerOptions =
    loanFile?.borrowers?.map((borrower) => ({
      label: `${borrower.firstName} ${borrower.lastName}`,
      value: borrower.id,
    })) || [];

  const fields = [
    {
      label: 'Type',
      type: 'select',
      name: 'type',
      options: LOAN_FILE_DOCUMENT_TYPES,
      placeholder: 'Select Type',
      validation: Yup.string().required('This field is required'),
    },
    {
      label: 'Borrower',
      type: 'select',
      name: 'borrowerId',
      options: borrowerOptions,
      placeholder: 'Select Borrower',
    },
    {
      label: 'Filename',
      type: 'text',
      name: 'filename',
      disabled: true,
      initialValue: file?.name,
    },
    {
      label: 'Document Date',
      type: 'date',
      name: 'date',
      placeholder: 'Select Date',
      isHidden: (values) => {
        return values.type === 'Bank Statement';
      },
    },
    {
      label: 'Notes',
      type: 'textarea',
      name: 'notes',
      placeholder: 'Enter notes',
    },
  ];

  const { mutate: createDocument, isPending: isCreating } = useMutation({
    mutationFn: (formValues) =>
      LoanFileService.createLoanFileDocument({
        id: loanFileId,
        formValues,
        file,
      }),
    onSuccess: () => {
      notify({
        type: 'success',
        message: 'Document created successfully',
      });
      setFiles((prevFiles) => {
        return prevFiles.filter((prevFile) => {
          return prevFile.name !== file.name;
        });
      });
      refetch();
    },
    onError: (error) => {
      notify({
        type: 'error',
        message: 'Error creating document',
      });
      console.log('Error creating document:', error);
    },
  });

  const { mutate: updateDocument, isPending: isUpdating } = useMutation({
    mutationFn: (formValues) =>
      LoanFileService.updateLoanFileDocument({
        id: loanFileId,
        documentId,
        formValues,
      }),
    onSuccess: () => {
      notify({
        type: 'success',
        message: 'Document updated successfully',
      });
      refetchDocument();
    },
    onError: (error) => {
      notify({
        type: 'error',
        message: 'Error updating document',
      });
      console.log('Error updating document:', error);
    },
  });

  return (
    <div>
      <TitleWithDeleteButton
        title={documentId ? 'Edit Document' : 'Document'}
        onDelete={
          documentId
            ? null
            : () => {
                setFiles((prevFiles) => {
                  return prevFiles.filter((prevFile) => {
                    return prevFile.name !== file.name;
                  });
                });
              }
        }
      />
      <CrudForm
        fields={fields}
        submitButtonLabel="Submit"
        createHandler={createDocument}
        disabled={disabled || isCreating || isUpdating}
        data={document}
        updateHandler={updateDocument}
      />
    </div>
  );
}
